// Step 2: the customer mapping file — which YouTrack project belongs to which
// customer. Read in the browser and sent as JSON; the server matches it to the
// scan and proposes a company and a project for every one. A BookStack batch
// arrives here with a mapping the scan generated: download it, edit it, and
// upload it back.
import { type DragEvent, useRef, useState } from "react"
import { Button, ErrorNote, useToast } from "../../ui"
import { api } from "../../../lib/api"
import { type Batch, UNASSIGNED } from "../../../lib/batchMigrations"
import { formatCount } from "../../../lib/migrations"
import { Note, StepLayout } from "../parts"

export function UploadStep({
  batch,
  onChange,
  onCancel,
  onContinue,
}: {
  batch: Batch
  onChange: (b: Batch) => void
  onCancel: () => void
  /** Back to the selection without uploading again. */
  onContinue?: () => void
}) {
  const toast = useToast()
  const bookstack = batch.source === "bookstack"
  const unit = bookstack ? "shelves" : "projects"
  const input = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const unassigned = batch.items.filter((i) => i.customer === UNASSIGNED).length

  const upload = async (file: File | undefined) => {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      let parsed: unknown
      try {
        parsed = JSON.parse(await file.text())
      } catch {
        throw new Error(`${file.name} is not valid JSON.`)
      }
      const { batch: next } = await api.put<{ batch: Batch }>(`/platform/migrations/batches/${batch.id}/mapping-file`, {
        name: file.name,
        file: parsed,
      })
      onChange(next)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ""
    }
  }

  const download = async () => {
    try {
      const json = await api.raw(`/platform/migrations/batches/${batch.id}/mapping-file.json`)
      const url = URL.createObjectURL(new Blob([json], { type: "application/json" }))
      const a = document.createElement("a")
      a.href = url
      a.download = "bookstack-customer-mapping.json"
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast("Could not download the mapping. Try again.", "error")
    }
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setDragging(false)
    void upload(e.dataTransfer.files[0])
  }

  return (
    <StepLayout
      title={bookstack ? "The customer mapping" : "Upload the customer mapping"}
      intro={
        bookstack
          ? `BookStack has ${formatCount(batch.items.length)} shelves. The scan proposed a customer for each; download the mapping to change it in bulk, then upload it back.`
          : `YouTrack has ${formatCount(batch.items.length)} projects. The mapping file says which customer each belongs to; every customer becomes a company.`
      }
      guide={{
        points: bookstack
          ? [
              'The format: { "customers": [ { "customer": "Abacus", "shelves": [ { "slug": "abacus" } ] } ] }. "company" names an existing company, "domain" a new one\'s domain.',
              "A customer with the name of an existing company goes into it; any other gets a new company.",
              "Shelves the file does not name stay Unassigned and unticked.",
            ]
          : [
              'The format of youtrack-customer-mapping.json: { "customers": [ { "customer": "Logista", "projects": [ { "key": "CLLSA" } ] } ] }.',
              "A customer with the name of an existing company goes into it; any other gets a new company.",
              "Projects the file does not name stay Unassigned and unticked. You can still give them a customer.",
            ],
        tip: "Uploading again replaces every choice made on the next step.",
      }}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel batch
          </Button>
          {batch.mappingFile && onContinue && (
            <Button type="button" className="ml-auto" onClick={onContinue}>
              Continue
            </Button>
          )}
        </>
      }
    >
      {bookstack && (
        <div>
          <Button type="button" variant="ghost" onClick={download}>
            Download the current mapping (JSON)
          </Button>
        </div>
      )}
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 text-center ${
          dragging ? "border-interactive bg-surface-muted" : "border-border"
        }`}
      >
        <p className="text-sm text-muted">Drop the mapping JSON here, or</p>
        <Button type="button" variant="ghost" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? "Reading…" : batch.mappingFile ? "Choose another file" : "Choose a file"}
        </Button>
        <input
          ref={input}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => void upload(e.target.files?.[0])}
        />
      </div>
      <ErrorNote error={error} />
      {batch.mappingFile && (
        <div className="flex flex-col gap-2">
          <Note tone="success">
            <b className="font-semibold">{batch.mappingFile.name}</b>: {formatCount(batch.mappingFile.customers)} customers,{" "}
            {formatCount(batch.mappingFile.projects)} {unit}. {formatCount(unassigned)}{" "}
            {bookstack ? (unassigned === 1 ? "shelf is" : "shelves are") : unassigned === 1 ? "YouTrack project is" : "YouTrack projects are"}{" "}
            not in it.
          </Note>
          {bookstack && (batch.mappingFile.unshelvedBooks ?? 0) > 0 && (
            <Note tone="warning">
              {formatCount(batch.mappingFile.unshelvedBooks ?? 0)} book{batch.mappingFile.unshelvedBooks === 1 ? " sits" : "s sit"} on no
              shelf, so a batch does not bring {batch.mappingFile.unshelvedBooks === 1 ? "it" : "them"}. Put them on a shelf in BookStack, or
              import them into a space by book slug.
            </Note>
          )}
          {batch.mappingFile.unknownKeys.length > 0 && (
            <Note tone="warning">
              Not found in {bookstack ? "BookStack" : "YouTrack"}: {batch.mappingFile.unknownKeys.join(", ")}. They are ignored.
            </Note>
          )}
        </div>
      )}
    </StepLayout>
  )
}
